import React, { useEffect, useState } from "react";
import {
  Flex,
  Text,
  Grid,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Box,
  Button,
  Input,
  IconButton,
  Select,
  useDisclosure,
} from "@chakra-ui/react";
import { EditIcon } from "@chakra-ui/icons";
import Card from "components/Card/Card.js";
import CardHeader from "components/Card/CardHeader.js";
import CardBody from "components/Card/CardBody.js";
import ProductApi from "api/product";
import { useParams } from "react-router-dom";
import UploadModal from "components/Uploader/UploadModal";
import EditAccountModal from "components/Modal/EditAccountModal";
import { useInView } from "react-intersection-observer";
import { debounce } from "lodash";

const ProductAccounts = ({ productId }) => {
  const [accounts, setAccounts] = useState([]);
  const [productName, setProductName] = useState([]);
  const [search, setSearch] = useState("");
  const { id } = useParams();
  const [orderBy, setOrderBy] = useState("updated_at");
  const [orderDirection, setOrderDirection] = useState("desc");
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);

  const fetchAccounts = async (pageNum = 1, reset = false) => {
    try {
      const response = await ProductApi.Accounts(id, { orderBy, orderDirection, page: pageNum, search });
      if (reset) {
        setAccounts(response.data.data); // Reset accounts when fetching new data
      } else {
        setAccounts((prevAccounts) => [...prevAccounts, ...response.data.data]); // Append data for infinite scroll
      }
      setProductName(response.data.product_name)
      setHasMore(response.data.data.length > 0);
    } catch (error) {
      console.error("Error fetching data: ", error);
    }
  };

  useEffect(() => {
    fetchAccounts(page);
  }, [page]);

  useEffect(() => {
    setPage(1);
    fetchAccounts(1, true);
  }, [search, orderBy, orderDirection]);

  const handleSearchChange = debounce((event) => {
    setSearch(event.target.value);
  }, 300); // Adding a 300ms debounce to reduce API calls on each keystroke

  const handleOrderByChange = (event) => {
    setOrderBy(event.target.value);
  };

  const handleOrderDirectionChange = (event) => {
    setOrderDirection(event.target.value);
  };

  const {
    isOpen: isUploadModalOpen,
    onOpen: onUploadModalOpen,
    onClose: onUploadModalClose,
  } = useDisclosure();

  const {
    isOpen: isEditModalOpen,
    onOpen: onEditModalOpen,
    onClose: onEditModalClose,
  } = useDisclosure();

  const [currentAccount, setCurrentAccount] = useState(null);

  const handleEditClick = (account) => {
    setCurrentAccount(account);
    onEditModalOpen();
  };

  const handleUploadSuccess = () => {
    fetchAccounts(1, true); // Reset and refetch after upload
  };

  const { ref: loadMoreRef, inView } = useInView({
    triggerOnce: false,
    threshold: 1.0,
  });

  useEffect(() => {
    if (inView && hasMore) {
      setPage((prevPage) => prevPage + 1);
    }
  }, [inView, hasMore]);

  return (
    <Flex direction="column" pt={{ base: "120px", md: "75px" }}>
      {/* Filters and actions */}
      <Card mb={6} p={4} boxShadow="md" borderRadius="xl" bg={"whiteAlpha.900"}>
        <Flex wrap="wrap" align="center" justify="space-between" gap={4}>
          <Box>
            <Text fontSize="xl" fontWeight="bold" display="inline">
              اکانت های {productName}
            </Text>
            <Text fontSize="sm" color="gray.500" ml={2} display="inline">
              (تعداد: {accounts.length})
            </Text>
          </Box>
          <Flex gap={2} flexWrap="wrap">
            <Button colorScheme="teal" leftIcon={<EditIcon />} size="sm" onClick={onUploadModalOpen}>
              آپلود فایل
            </Button>
            <Select width="150px" value={orderBy} onChange={handleOrderByChange} fontSize="sm">
              <option value="email">نام</option>
              <option value="created_at">تاریخ ایجاد</option>
              <option value="updated_at">آخرین تغییر</option>
            </Select>
            <Select width="100px" value={orderDirection} onChange={handleOrderDirectionChange} fontSize="sm">
              <option value="asc">صعودی</option>
              <option value="desc">نزولی</option>
            </Select>
            <Box position="relative">
              <Input
                placeholder="جستجو..."
                defaultValue={search}
                onChange={handleSearchChange}
                pr={8}
                size="sm"
                borderRadius="md"
                bg="gray.50"
              />
            </Box>
          </Flex>
        </Flex>
        <UploadModal
          isOpen={isUploadModalOpen}
          onClose={onUploadModalClose}
          onUploadSuccess={handleUploadSuccess}
          productId={id}
        />
      </Card>
      {/* Accounts grid */}
      <Grid
        templateColumns={{ base: "1fr", sm: "repeat(2, 1fr)", md: "repeat(3, 1fr)", lg: "repeat(4, 1fr)" }}
        gap={6}
        p={{ base: 2, md: 4 }}
        width="100%"
      >
        {accounts.map((account) => {
          // Flag emoji for region
          const flag = account.region === 'us' ? '🇺🇸' : account.region === 'tr' ? '🇹🇷' : account.region ? account.region.toUpperCase() : '';
          return (
            <Box
              as={Card}
              key={account.id}
              w="100%"
              borderWidth="1px"
              borderRadius="2xl"
              boxShadow="lg"
              p={0}
              bgGradient="linear(to-br, white, gray.50, teal.50)"
              position="relative"
              overflow="hidden"
              _hover={{ boxShadow: "2xl", transform: "translateY(-2px) scale(1.01)", transition: "0.2s" }}
            >
              {/* Modern header with faded flag watermark */}
              <Box
                as={CardHeader}
                position="relative"
                bgGradient="linear(to-r, teal.400, teal.600)"
                color="white"
                p={4}
                borderTopRadius="2xl"
                minHeight="80px"
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                boxShadow="sm"
              >
                {/* Faded flag watermark */}
                <Box
                  position="absolute"
                  left={2}
                  top={2}
                  fontSize="60px"
                  opacity={0.10}
                  zIndex={0}
                  pointerEvents="none"
                  userSelect="none"
                >
                  {flag}
                </Box>
                {/* Left: small flag + email */}
                <Flex align="center" zIndex={1}>
                  <Box fontSize="xl" mr={2}>{flag}</Box>
                  <Text fontWeight="medium" fontSize="sm" color="white" wordBreak="break-all" textAlign="left">
                    {account.email}
                  </Text>
                </Flex>
                {/* Right: edit button */}
                <IconButton
                  aria-label="ویرایش"
                  icon={<EditIcon color="whiteAlpha.800" />} // Make pencil icon light
                  size="sm"
                  colorScheme="whiteAlpha"
                  variant="ghost"
                  onClick={() => handleEditClick(account)}
                  _hover={{ bg: "whiteAlpha.300" }}
                  zIndex={1}
                />
              </Box>
              <Box as={CardBody} p={0} overflowY="auto">
                <Table variant="simple" size="sm" w="100%" textAlign="center">
                  <Thead>
                    <Tr>
                      <Th py="2" fontSize="xs">پر؟</Th>
                      <Th py="2" fontSize="xs">سفارش</Th>
                      <Th py="2" fontSize="xs">ظرفیت</Th>
                      <Th py="2" fontSize="xs">پلتفرم</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {account.statuses.map((status) => (
                      <Tr key={status.id}>
                        <Td py="1" textAlign="center">
                          {status.is_full ? <Box as="span" color="green.400" fontSize="lg">●</Box> : <Box as="span" color="gray.300" fontSize="lg">○</Box>}
                        </Td>
                        <Td py="1">{status.worder_id ?? "-"}</Td>
                        <Td py="1" fontWeight="bold" color="gray.700">{status.name}</Td>
                        <Td py="1">
                          <Box
                            as="span"
                            px={2}
                            py={0.5}
                            borderRadius="md"
                            fontSize="xs"
                            fontWeight="bold"
                            color="white"
                            bg={status.platform === 1 ? "teal.500" : status.platform === 2 ? "pink.500" : "gray.400"}
                          >
                            {status.platform === 1 ? "PS4" : status.platform === 2 ? "PS5" : "-"}
                          </Box>
                        </Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              </Box>
            </Box>
          );
        })}
      </Grid>
      {hasMore && (
        <Box ref={loadMoreRef} textAlign="center" mt="4">
          <Text>در حال بارگذاری...</Text>
        </Box>
      )}
      {currentAccount && (
        <EditAccountModal
          isOpen={isEditModalOpen}
          onClose={onEditModalClose}
          accountId={currentAccount.id}
          productId={id}
        />
      )}
    </Flex>
  );
};

export default ProductAccounts;
